/**
 * Generischer Inline-Editor fuer Personalakte-Subressourcen (Rufnummern,
 * E-Mails, Adressen, Stellenzuordnungen) — Muster: MasterDataSection +
 * EntityFormSheet, aber mit employeeId-gebundenem CRUD.
 */

import { useState, type ReactNode } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import {
  EntityFormSheet,
  type FieldDef,
  type FormValues,
} from "@/components/admin/EntityFormSheet"
import { MasterDataSection, type Column } from "@/components/admin/MasterDataSection"
import { apiError } from "@/services/api"

export interface SubCrud<T extends { id: string }, C> {
  list: (employeeId: string) => Promise<T[]>
  create: (employeeId: string, body: C) => Promise<T>
  update: (employeeId: string, id: string, body: Partial<C>) => Promise<T>
  remove: (employeeId: string, id: string) => Promise<void>
}

interface SubTableEditorProps<T extends { id: string }, C> {
  employeeId: string
  /** Query-Key-Segment, z. B. "phone-numbers" */
  queryKey: string
  crud: SubCrud<T, C>
  columns: Column<T>[]
  fields: FieldDef[]
  entityLabel: string
  createLabel: string
  emptyText: string
  canWrite: boolean
  /** Zeile → Dialog-Startwerte */
  toValues: (row: T | null) => FormValues
  /** Dialog-Werte → API-Payload */
  fromValues: (values: FormValues) => C
  /** Zusaetzliche Zeilen-Aktionen (z. B. „Als Standard setzen") */
  rowActions?: (row: T) => ReactNode
}

export function SubTableEditor<T extends { id: string }, C>({
  employeeId,
  queryKey,
  crud,
  columns,
  fields,
  entityLabel,
  createLabel,
  emptyText,
  canWrite,
  toValues,
  fromValues,
  rowActions,
}: SubTableEditorProps<T, C>) {
  const queryClient = useQueryClient()
  const fullKey = ["employee", employeeId, queryKey]
  const query = useQuery({ queryKey: fullKey, queryFn: () => crud.list(employeeId) })
  const [dialog, setDialog] = useState<{ open: boolean; row: T | null }>({
    open: false,
    row: null,
  })

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: fullKey })
    // Detail-Zaehler (counters) aktualisieren
    queryClient.invalidateQueries({ queryKey: ["employee", employeeId, "detail"] })
  }

  const save = useMutation({
    mutationFn: ({ id, body }: { id: string | null; body: C }) =>
      id ? crud.update(employeeId, id, body) : crud.create(employeeId, body),
    onSuccess: (_data, vars) => {
      invalidate()
      toast.success(vars.id ? `${entityLabel} aktualisiert` : `${entityLabel} angelegt`)
      setDialog({ open: false, row: null })
    },
    onError: (e) => toast.error(apiError(e)),
  })

  const del = useMutation({
    mutationFn: (id: string) => crud.remove(employeeId, id),
    onSuccess: () => {
      invalidate()
      toast.success(`${entityLabel} gelöscht`)
    },
    onError: (e) => toast.error(apiError(e)),
  })

  return (
    <>
      <MasterDataSection
        isLoading={query.isLoading}
        rows={query.data ?? []}
        columns={columns}
        storageKey={`masterdata-${queryKey}`}
        emptyText={emptyText}
        canWrite={canWrite}
        canDelete={canWrite}
        createLabel={createLabel}
        onCreate={() => setDialog({ open: true, row: null })}
        onEdit={(row) => setDialog({ open: true, row })}
        onDelete={(row) => {
          if (window.confirm(`${entityLabel} wirklich löschen?`)) del.mutate(row.id)
        }}
        deletePending={del.isPending}
        rowActions={rowActions}
      />
      <EntityFormSheet
        sheetKey="entity-form-employee-subtable"
        open={dialog.open}
        onOpenChange={(open) => setDialog((d) => ({ ...d, open }))}
        title={dialog.row ? `${entityLabel} bearbeiten` : `${entityLabel} anlegen`}
        fields={fields}
        initialValues={toValues(dialog.row)}
        pending={save.isPending}
        onSubmit={(values) =>
          save.mutate({ id: dialog.row?.id ?? null, body: fromValues(values) })
        }
      />
    </>
  )
}
